import { useState, useEffect, type FormEvent } from "react";
import { useSignIn, useAuth } from "@clerk/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { ArrowRight, LogIn } from "lucide-react";
import "./sign-in.css";

type Step = "credentials" | "verify";

export default function SignInPage() {
  const { signIn, errors, fetchStatus } = useSignIn();
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [revealed, setRevealed] = useState(false);
  const [step, setStep] = useState<Step>("credentials");
  const [code, setCode] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    requestAnimationFrame(() => setRevealed(true));
  }, []);

  useEffect(() => {
    if (isSignedIn) {
      navigate({ to: "/profile" });
    }
  }, [isSignedIn, navigate]);

  async function finalizeSignIn() {
    await signIn.finalize({
      navigate: ({ decorateUrl }) => {
        const url = decorateUrl("/profile");
        if (url.startsWith("http")) {
          window.location.href = url;
        } else {
          navigate({ to: "/profile" });
        }
      },
    });
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);

    const form = new FormData(e.currentTarget);
    const identifier = (form.get("email") as string).trim();
    const password = form.get("password") as string;

    await signIn.password({ identifier, password });

    if (signIn.status === "complete") {
      await finalizeSignIn();
      return;
    }

    if (
      signIn.status === "needs_client_trust" ||
      signIn.status === "needs_second_factor"
    ) {
      await signIn.mfa.sendEmailCode();
      setStep("verify");
    }
  }

  async function handleVerify(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);

    await signIn.mfa.verifyEmailCode({ code });

    if (signIn.status === "complete") {
      await finalizeSignIn();
    }
  }

  if (isSignedIn) return null;

  const isBusy = fetchStatus === "fetching";

  return (
    <div className="auth-page">
      <section className={cn("auth-section", revealed && "revealed")}>
        <div className="auth-card">
          {step === "verify" ? (
            <>
              <div className="auth-header">
                <h1 className="auth-heading">Check your email</h1>
                <p className="auth-subtext">
                  We sent a verification code to confirm it&apos;s you.
                </p>
              </div>

              <form className="auth-form" onSubmit={handleVerify}>
                <div className="auth-field">
                  <label className="auth-label" htmlFor="auth-code">
                    Verification code
                  </label>
                  <input
                    id="auth-code"
                    type="text"
                    className="auth-input"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="Enter 6-digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                  />
                  {errors?.fields?.code && (
                    <p className="auth-field-error">{errors.fields.code.message}</p>
                  )}
                </div>

                {formError && <div className="auth-error">{formError}</div>}
                {errors?.global?.map((err, i) => (
                  <div key={i} className="auth-error">{err.message}</div>
                ))}

                <button type="submit" className="auth-submit" disabled={isBusy}>
                  {isBusy ? "Verifying..." : "Verify"}
                  {!isBusy && <ArrowRight className="size-4" />}
                </button>

                <button
                  type="button"
                  className="auth-link-btn"
                  onClick={() => signIn.mfa.sendEmailCode()}
                >
                  Resend code
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="auth-header">
                <LogIn className="auth-icon" />
                <h1 className="auth-heading">Welcome back</h1>
                <p className="auth-subtext">
                  Sign in to your Tomorrow&apos;s Run account.
                </p>
              </div>

              <form className="auth-form" onSubmit={handleSubmit}>
                <div className="auth-field">
                  <label className="auth-label" htmlFor="auth-email">
                    Email address
                  </label>
                  <input
                    id="auth-email"
                    name="email"
                    type="email"
                    className="auth-input"
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                  />
                  {errors?.fields?.identifier && (
                    <p className="auth-field-error">{errors.fields.identifier.message}</p>
                  )}
                </div>

                <div className="auth-field">
                  <label className="auth-label" htmlFor="auth-password">
                    Password
                  </label>
                  <input
                    id="auth-password"
                    name="password"
                    type="password"
                    className="auth-input"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    required
                  />
                  {errors?.fields?.password && (
                    <p className="auth-field-error">{errors.fields.password.message}</p>
                  )}
                </div>

                {formError && <div className="auth-error">{formError}</div>}
                {errors?.global?.map((err, i) => (
                  <div key={i} className="auth-error">{err.message}</div>
                ))}

                <button type="submit" className="auth-submit" disabled={isBusy}>
                  {isBusy ? "Signing in..." : "Sign in"}
                  {!isBusy && <ArrowRight className="size-4" />}
                </button>

                <p className="auth-footer-text">
                  Don&apos;t have an account?{" "}
                  <Link to="/sign-up" className="auth-link">
                    Create one
                  </Link>
                </p>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
